import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import useMeetingReminder from "../hooks/useMeetingReminder";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDateLabel } from "../utils";
import { Helmet } from 'react-helmet-async';
import axiosInstance from "../utils/axiosInstance";

const MeetingsList = () => {
  const { userId, token } = useContext(AuthContext);
  const [meetings, setMeetings] = useState([]);
  const [editMeetingId, setEditMeetingId] = useState(null);
  const [editedTime, setEditedTime] = useState(new Date());
  const [editedLink, setEditedLink] = useState("");

  useMeetingReminder(meetings, userId);

  useEffect(() => {
    fetchMeetings();
  }, [userId, token]);

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/meetings/${userId}`,
        { headers: { Authorization: token } }
      );
      setMeetings(res.data);
    } catch (err) {
      console.error("❌ Failed to load meetings:", err);
    }
  };

  const cancelMeeting = async (id) => {
    if (!window.confirm("Cancel this meeting?")) return;
    try {
      await axiosInstance.delete(`${process.env.REACT_APP_API_BASE}/api/meetings/${id}`, {
        headers: { Authorization: token },
      });
      fetchMeetings();
    } catch (err) {
      toast.error("❌ Could not cancel meeting");
    }
  };

  const startEdit = (meeting) => {
    setEditMeetingId(meeting._id);
    setEditedTime(new Date(meeting.time));
    setEditedLink(meeting.link || "");
  };

  const saveEdit = async () => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE}/api/meetings/${editMeetingId}`,
        { time: editedTime, link: editedLink },
        { headers: { Authorization: token } }
      );
      setEditMeetingId(null);
      fetchMeetings();
    } catch (err) {
      toast.error("❌ Failed to update meeting");
    }
  };

  if (!meetings.length) {
    return (
      <div className="text-center text-gray-500 text-sm mt-6">
        No scheduled meetings.
      </div>
    );
  }

  return (
    <div className="space-y-8 mt-4">
      <Helmet>
        <title>Meeting Lists - PairLingo</title>
        <meta name="description" content="Speak with perfect language partner on PairLingo." />
        <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
      </Helmet>
      {Object.entries(
        meetings.reduce((groups, meeting) => {
          const label = getDateLabel(meeting.time);
          if (!groups[label]) groups[label] = [];
          groups[label].push(meeting);
          return groups;
        }, {})
      ).map(([label, group]) => (
        <div key={label}>
          <h3 className="text-md font-semibold text-indigo-700 mb-3">{label}</h3>

          <div className="space-y-4">
            {group.map((m) => {
              const isEditing = editMeetingId === m._id;
              const withUser =
                m.organizer._id === userId
                  ? m.participant.username
                  : m.organizer.username;

              return (
                <div
                  key={m._id}
                  className="bg-white rounded-xl shadow p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                >
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="space-y-2">
                        <p className="text-sm">
                          <strong>Editing with:</strong>{" "}
                          <span className="text-indigo-600 font-medium">{withUser}</span>
                        </p>
                        <DatePicker
                          selected={editedTime}
                          onChange={(date) => setEditedTime(date)}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="Pp"
                          className="border px-2 py-1 rounded w-full"
                        />
                        <input
                          type="text"
                          value={editedLink}
                          onChange={(e) => setEditedLink(e.target.value)}
                          placeholder="Meeting link"
                          className="border px-2 py-1 rounded w-full"
                        />
                      </div>
                    ) : (
                      <div className="space-y-1 text-sm">
                        <p>
                          <strong>With:</strong>{" "}
                          <span className="text-indigo-600 font-medium">{withUser}</span>
                        </p>
                        <p>
                          <strong>Time:</strong>{" "}
                          {new Date(m.time).toLocaleString([], {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </p>
                        {m.link && (
                          <p className="break-words">
                            <strong>Link:</strong>{" "}
                            <a
                              href={m.link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              {m.link}
                            </a>
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveEdit}
                          className="text-sm bg-indigo-600 text-white px-4 py-1 rounded hover:bg-indigo-500"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditMeetingId(null)}
                          className="text-sm text-gray-500 hover:text-red-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(m)}
                          className="text-sm bg-yellow-100 text-yellow-700 px-4 py-1 rounded hover:bg-yellow-200"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => cancelMeeting(m._id)}
                          className="text-sm bg-red-100 text-red-600 px-4 py-1 rounded hover:bg-red-200"
                        >
                          ❌ Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MeetingsList;